import { useEffect, useRef, useState } from "react";
import "./App.css";

import { CSSTransition } from "react-transition-group";

import LeftPanel from "./sections/LeftPanel";
import MapSection from "./sections/MapSection";
import RightBar from "./sections/RightBar";
import DMInfoPane from "./sections/DMInfoPane";
import {
  checkAuth,
  generateMap,
  getMapUrl,
  getPatreonTier,
  login,
  logout,
} from "./api";

const App = () => {
  const [infoActive, setInfoActive] = useState(false);
  const [settingsActive, setSettingsActive] = useState(false);
  const [emailActive, setEmailActive] = useState(false);
  const [helperEnabled, setHelperEnabled] = useState(false);
  const [showHelper, setShowHelper] = useState(false);
  const [playerMapUrl, setPlayerMapUrl] = useState();
  const [dmMapUrl, setDmMapUrl] = useState();
  const [dmPdfUrl, setDmPdfUrl] = useState();
  const [basementMapUrl, setBasementMapUrl] = useState();
  const [mapData, setMapData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const generateHelperRef = useRef(null);

  const queryParams = new URLSearchParams(window.location.search);
  const emailMode = queryParams.get("email");

  const rightPaneOpen = infoActive || emailActive;

  const mobileUserAgent =
    /android|ipad|iphone/.test(navigator.userAgent.toLowerCase()) &&
    !window.MSStream;

  const handleLogin = () => {
    login();
  };

  const handleLogout = () => {
    const confirmLogout = window.confirm(
      "Log out of Mythical Maps Infinity?\n\nPress OK to log out, or Cancel to stay logged in."
    );
    if (!confirmLogout) return;

    logout();
  };

  useEffect(() => {
    setShowHelper(helperEnabled);
  }, [helperEnabled]);

  // Check if the user is authenticated when the app loads
  useEffect(() => {
    checkAuth()
      .then((data) => {
        if (data.isAuthenticated) {
          getPatreonTier()
            .then(({ tier }) => {
              setUser({ ...data.user, tier });
              setIsAuthenticated(true);
              setHelperEnabled(false);
            })
            .catch((error) => {
              console.error("Error getting Patreon tier:", error);
              alert(
                "You need to be a subscriber to the Arcane Collector Patreon to gain full access to Mythical Maps Infinity. Please subscribe and refresh the page to gain full access."
              );
            });
        } else {
          if (!mobileUserAgent) {
            setHelperEnabled(true);
          }
        }
      })
      .catch((error) => {
        if (!mobileUserAgent) {
          setHelperEnabled(true);
        }
        console.error("Error checking authentication:", error);
      });
  }, []);

  const handleGenerateMap = async (state) => {
    setHelperEnabled(false);

    setSettingsActive(true);

    if (loading) return;

    // console.log("formState", state);
    setLoading(true);
    setError(null);
    setMapData([]);
    setDmMapUrl("");
    setPlayerMapUrl("");
    setDmPdfUrl("");
    setBasementMapUrl("");

    try {
      const mapData = await generateMap(state);

      // console.log("MAPDATA", mapData);

      const playerMapUrl = await getMapUrl(mapData?.filenames?.player);
      setPlayerMapUrl(playerMapUrl);

      if (mapData?.filenames?.dm) {
        const dmMapUrl = await getMapUrl(mapData?.filenames?.dm);
        setDmMapUrl(dmMapUrl);
      }

      if (mapData?.filenames?.pdf) {
        const dmPdfUrl = await getMapUrl(mapData?.filenames?.pdf);
        setDmPdfUrl(dmPdfUrl);
      }

      if (mapData?.filenames?.basement) {
        const basementMapUrl = await getMapUrl(mapData?.filenames?.basement);
        setBasementMapUrl(basementMapUrl);
      }

      setMapData(mapData);
      if (mapData?.filenames?.dm) {
        setEmailActive(false);
      }

      if (!isMobile) {
        setInfoActive(true);
      }
    } catch (error) {
      setError(error.toString());
    } finally {
      setLoading(false);
    }
  };

  const handleInfoClick = () => {
    if (!infoActive) {
      setEmailActive(false);
    }

    setInfoActive(!infoActive);
  };

  const handleSettingsClick = () => {
    setSettingsActive(!settingsActive);
  };

  const handleEmailClick = () => {
    if (!emailActive) {
      setInfoActive(false);
    }

    setEmailActive(!emailActive);
  };

  useEffect(() => {
    if (mobileUserAgent) {
      setIsMobile(true);
    }
  }, []);

  useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setViewportHeight();
    window.addEventListener("resize", setViewportHeight);

    return () => {
      window.removeEventListener("resize", setViewportHeight);
    };
  }, []);

  return (
    <div className="App">
      <header className="mobileHeader">
        <div className="headerContent">
          <img
            src="images/settings-icon.svg"
            alt="Settings"
            onClick={handleSettingsClick}
            style={{ cursor: "pointer", verticalAlign: "middle" }}
            className="settingsIcon"
          />
          <span
            className="subTitle"
            style={{ display: "inline-block", textAlign: "center" }}
          >
            Mythical Maps
          </span>
          {playerMapUrl ? (
            <img
              src="images/right-info-active.svg"
              alt="Map"
              onClick={handleInfoClick}
              style={{ cursor: "pointer", verticalAlign: "middle" }}
              className="infoIcon"
            />
          ) : (
            <span className="settingsIcon" />
          )}
        </div>
      </header>
      <div className="body">
        <div className={`leftContainer ${settingsActive ? "active" : ""}`}>
          <LeftPanel
            generateMap={handleGenerateMap}
            loading={loading}
            isAuthenticated={isAuthenticated}
            handleLogin={handleLogin}
            userTier={user?.tier || "FREE"}
            setShowHelper={setShowHelper}
            user={user}
            handleLogout={handleLogout}
          />
        </div>
        <MapSection
          rightPaneOpen={rightPaneOpen}
          playerMapUrl={playerMapUrl}
          dmMapUrl={dmMapUrl}
          dmPdfUrl={dmPdfUrl}
          basementMapUrl={basementMapUrl}
          mapData={mapData}
          loading={loading}
          error={error}
        />
        <div className={`rightContainer ${rightPaneOpen ? "active" : ""}`}>
          <RightBar
            infoActive={infoActive}
            showEmail={emailMode}
            emailActive={emailActive}
            handleInfoClick={handleInfoClick}
            handleEmailClick={handleEmailClick}
          />
          <DMInfoPane
            mapData={mapData}
            emailActive={emailActive}
            handleInfoClick={handleInfoClick}
          />
        </div>
      </div>
      <CSSTransition
        in={helperEnabled && showHelper}
        timeout={1000}
        nodeRef={generateHelperRef}
        classNames="fadeLong"
        unmountOnExit
      >
        <div ref={generateHelperRef}>
          <label className="generateHelperLabel">
            Click <span style={{ fontWeight: "bold" }}>Generate Map</span> to
            <br />
            get started
          </label>
          <img
            src="images/generate-arrow.png"
            className="generateHelperArrow"
          />
        </div>
      </CSSTransition>
      {/* <div className="mobile-message">
        This application is not available on mobile devices at this time. To
        learn more about the App please visit{" "}
        <a href="https://arcanecollector.com/mythical-maps-infinity-information/">
          https://arcanecollector.com/mythical-maps-infinity-information/
        </a>
      </div> */}
    </div>
  );
};

export default App;
